import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { Trans, useTranslation } from "react-i18next";
import { successToast, errorToast } from "../../../utils/toasts";

/**
 * Reads the one-shot OAuth-callback query params the backend appends when redirecting back to
 * /user/walmartsummary, fires a success/error toast, and strips the query string. The `handledRef`
 * guard keeps the toast firing once under StrictMode's synchronous double-invoke (and makes the
 * re-run when the replace navigation clears the search a no-op).
 */
export function useWalmartOAuthCallbackToast() {
  const { t } = useTranslation(["onboarding"]);
  const navigate = useNavigate();
  const searchStr = useLocation({ select: (l) => l.searchStr });
  const handledRef = useRef(false);

  useEffect(() => {
    if (handledRef.current) return;
    handledRef.current = true;
    const params = new URLSearchParams(searchStr);
    if (params.get("walmart_linked") === "1") {
      const orgName = params.get("org_name") ?? "";
      const sellerName = params.get("seller_name") ?? "";
      const createdNewOrg = params.get("created_new_org") === "1";
      const fallbackSeller = t(($) => $.onboarding["your Walmart account"]);
      const title = t(($) => $.onboarding["Walmart account linked"]);
      const message =
        createdNewOrg ?
          <Trans
            t={t}
            i18nKey={($) => $.onboarding["Linked <strong>{seller}</strong> to new workspace <strong>{org}</strong>."]}
            values={{ seller: sellerName || fallbackSeller, org: orgName }}
            components={{ strong: <strong /> }}
          />
        : <Trans
            t={t}
            i18nKey={($) => $.onboarding["Linked <strong>{seller}</strong>."]}
            values={{ seller: sellerName || orgName || fallbackSeller }}
            components={{ strong: <strong /> }}
          />;
      successToast({ title, message });
      void navigate({ to: ".", search: {}, replace: true, resetScroll: false });
    } else if (params.get("error")) {
      const code = params.get("error") ?? "";
      const detail = params.get("detail") ?? undefined;
      const errorMessages: Record<string, string> = {
        state_invalid: t(
          ($) => $.onboarding["The connection request expired or was invalid. Please try connecting again."],
        ),
        seller_id_mismatch: t(
          ($) => $.onboarding["This Walmart seller doesn't match the organization you tried to connect."],
        ),
        agency_conflict: t(
          ($) =>
            $.onboarding[
              "This Walmart seller is already connected to another organization in your agency. Contact your administrator to resolve."
            ],
        ),
        token_exchange_failed: t(
          ($) => $.onboarding["We couldn't complete the connection with Walmart. Please try again."],
        ),
        onboarding_failed: t(
          ($) =>
            $.onboarding[
              "The connection completed but onboarding could not finish. Please try again or contact support."
            ],
        ),
      };
      const message = errorMessages[code] ?? detail ?? t(($) => $.onboarding["Could not connect to Walmart."]);
      errorToast({ title: t(($) => $.onboarding["Walmart connection failed"]), message });
      void navigate({ to: ".", search: {}, replace: true, resetScroll: false });
    }
  }, [searchStr, navigate, t]);
}
